import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { VerdictBadge } from "@/components/RiskBadge";
import BackButton from "@/components/BackButton";
import { Search } from "lucide-react";

const FILTERS = [
  { v: "all", label: "All" },
  { v: "high_risk", label: "High Risk" },
  { v: "suspicious", label: "Suspicious" },
  { v: "clear", label: "Clear" },
];

export default function Scans() {
  const [scans, setScans] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => api.get("/scans").then(({ data }) => setScans(data)).catch(() => {});
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  const filtered = useMemo(() => {
    return scans.filter((s) => {
      if (filter !== "all" && s.verdict !== filter) return false;
      if (q && !`${s.candidate_name} ${s.hostname} ${s.interview_id}`.toLowerCase().includes(q.toLowerCase()))
        return false;
      return true;
    });
  }, [scans, filter, q]);

  return (
    <div>
      <BackButton />
      <div className="mb-8">
        <p className="eyebrow mb-2">FORENSIC RESULTS</p>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Scan Results</h1>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
          <input
            data-testid="scans-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search candidate, host, interview…"
            className="w-full bg-[#0B1220] border border-white/10 rounded-lg pl-10 pr-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
          />
        </div>
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.v}
              data-testid={`filter-${f.v}`}
              onClick={() => setFilter(f.v)}
              className={`px-3.5 py-2.5 rounded-lg text-xs font-mono border transition-colors ${
                filter === f.v
                  ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300"
                  : "border-white/10 text-slate-400 hover:border-white/25"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card-soc overflow-hidden">
        {filtered.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-500 text-sm">No matching scans.</div>
        ) : (
          <table className="w-full text-sm" data-testid="scans-table">
            <thead>
              <tr className="text-left eyebrow border-b border-white/8">
                <th className="px-6 py-3 font-normal">Candidate</th>
                <th className="px-6 py-3 font-normal">Host / OS</th>
                <th className="px-6 py-3 font-normal">Interview</th>
                <th className="px-6 py-3 font-normal">Detections</th>
                <th className="px-6 py-3 font-normal">Verdict</th>
                <th className="px-6 py-3 font-normal">Score</th>
                <th className="px-6 py-3 font-normal">When</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr
                  key={s.id}
                  onClick={() => navigate(`/scans/${s.id}`)}
                  data-testid={`scan-row-${s.id}`}
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-3.5 text-slate-100 font-medium">{s.candidate_name}</td>
                  <td className="px-6 py-3.5 text-slate-400 text-xs font-mono">
                    {s.hostname || "—"} · {s.os_name || "?"}
                  </td>
                  <td className="px-6 py-3.5 text-slate-400 font-mono text-xs">{s.interview_id || "—"}</td>
                  <td className="px-6 py-3.5 text-slate-300">{s.detections?.length ?? 0}</td>
                  <td className="px-6 py-3.5"><VerdictBadge verdict={s.verdict} /></td>
                  <td className="px-6 py-3.5 font-mono text-slate-200">{s.risk_score}</td>
                  <td className="px-6 py-3.5 text-slate-500 text-xs">
                    {new Date(s.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
